import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';

import { useZone } from './zone';

/**
 * The one status chip both portals draw (the two-portals design, §5).
 *
 * A small, fixed set of tones, and each vocabulary maps its values onto one of them in a table
 * of its own — the chip never decides what a value means, and it always shows the value's own
 * word. Every tone is the brand palette or a `color-mix` over it:
 *
 * | tone | means | colour |
 * | --- | --- | --- |
 * | `neutral` | a plain state: not started, locked, a kind | Royal Blue 15 % edge, Royal 70 % text |
 * | `info` | under way: in progress, a draft | Slight Blue fill, Royal Blue text |
 * | `success` | done or confirmed: submitted, verified | Teal 10 % fill, Teal text (4.5:1) |
 * | `attention` | needs a look, not wrong: needs attention, unverified | Deep Magenta edge and text on an 8 % Magenta wash |
 * | `danger` | refused or denied | Deep Magenta fill, white text (11.4:1) |
 * | `mock` | invented data: the demo, and the website content admin | the guideline Yellow, dashed at its darkened edge |
 *
 * `data-slot="badge"` because it replaces the kit's `Badge`, and the suite finds a card's badge by
 * that slot (`e2e/pages/person-hub.page.ts`).
 */
export type ChipTone = 'neutral' | 'info' | 'success' | 'attention' | 'danger' | 'mock';

const TONE: Record<ChipTone, string> = {
  neutral: 'border-[var(--veritant-royal-15)] bg-white text-[var(--veritant-royal-70)]',
  info: 'border-transparent bg-[var(--color-slight-blue)] text-[var(--color-royal-blue)]',
  success:
    'border-transparent bg-[color-mix(in_srgb,var(--color-teal)_10%,white)] text-[var(--color-teal)]',
  attention:
    'border-[color-mix(in_srgb,var(--color-deep-magenta)_35%,white)] bg-[color-mix(in_srgb,var(--color-magenta)_8%,white)] text-[var(--color-deep-magenta)]',
  danger: 'border-transparent bg-[var(--color-deep-magenta)] text-white',
  mock: 'border-dashed border-[var(--veritant-yellow-edge)] bg-[color-mix(in_srgb,var(--color-yellow)_30%,white)] text-[var(--color-midnight-blue)]'
};

export function StatusChip({
  tone = 'neutral',
  dot = false,
  wrap = false,
  className,
  children
}: {
  tone?: ChipTone;
  /** A leading dot in the chip's colour — for a state under way or settled, not for a kind. */
  dot?: boolean;
  /** Wraps rather than truncates — a value that must be read whole, like a registry number,
   *  which can be longer than a phone is wide. */
  wrap?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const dense = useZone() === 'team';
  return (
    <span
      data-slot="badge"
      data-tone={tone}
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-full border font-medium',
        wrap ? 'min-h-5 shrink py-px whitespace-normal' : 'shrink-0 whitespace-nowrap',
        dense ? 'px-2 text-[0.71875rem]' : 'px-2.5 text-xs',
        !wrap && (dense ? 'h-5' : 'h-6'),
        wrap && !dense && 'min-h-6',
        TONE[tone],
        className
      )}
    >
      {dot && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />}
      <span className={wrap ? 'min-w-0' : 'truncate'}>{children}</span>
    </span>
  );
}
